import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import I18n from 'i18n-js'
import patientService from '../../../../services/patients'

const STATUS_COLOR = {
  accepted: 'success',
  pending: 'info',
  expired: 'default',
}

const formatDate = (iso) => {
  if (!iso) return '-'
  try {
    return format(parseISO(iso), I18n.t('general.dateFormat') || 'dd-MM-yyyy')
  } catch {
    return '-'
  }
}

const ViewerInvitations = ({ patientId }) => {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!patientId) {
      setLoading(false)
      return
    }
    let cancelled = false
    const fetch = async () => {
      setLoading(true)
      const res = await patientService.getViewerInvitations(patientId)
      if (cancelled) return
      if (res?.data) {
        const items = Array.isArray(res.data) ? res.data : (res.data?.data ?? [])
        setInvitations(items)
      } else {
        setError(true)
      }
      setLoading(false)
    }
    fetch()
    return () => { cancelled = true }
  }, [patientId])

  const renderBody = () => {
    if (loading) {
      return (
        <AccordionDetails>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        </AccordionDetails>
      )
    }

    if (error) {
      return (
        <AccordionDetails>
          <Typography variant="body1" color="error">
            {I18n.t('general.deleteError')}
          </Typography>
        </AccordionDetails>
      )
    }

    if (!invitations.length) {
      return (
        <AccordionDetails>
          <Typography variant="body1" color="textPrimary">
            {I18n.t('general.notFound')}
          </Typography>
        </AccordionDetails>
      )
    }

    return (
      <AccordionDetails sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colStatus')}</TableCell>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colRecipient')}</TableCell>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colEmail')}</TableCell>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colRole')}</TableCell>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colCreated')}</TableCell>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colExpires')}</TableCell>
              <TableCell>{I18n.t('patient.detail.viewerInvitations.colDoctor')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((inv) => (
              <TableRow key={inv.id} hover>
                <TableCell>
                  <Chip
                    label={I18n.t(`patient.detail.viewerInvitations.status_${inv.status}`) || inv.status}
                    color={STATUS_COLOR[inv.status] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {inv.viewer
                    ? `${inv.viewer.name ?? ''} ${inv.viewer.lastname ?? ''}`.trim()
                    : inv.email}
                </TableCell>
                <TableCell>{inv.email ?? '-'}</TableCell>
                <TableCell>
                  {inv.viewer_role === 'self'
                    ? I18n.t('patient.detail.viewerInvitations.roleSelf')
                    : I18n.t('patient.detail.viewerInvitations.roleOther')}
                </TableCell>
                <TableCell>{formatDate(inv.created_at)}</TableCell>
                <TableCell>{formatDate(inv.expires_at)}</TableCell>
                <TableCell>
                  {inv.doctor
                    ? `${inv.doctor.name ?? ''} ${inv.doctor.lastname ?? ''}`.trim()
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    )
  }

  return (
    <Accordion elevation={2}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6" color="primary">
          {I18n.t('patient.detail.viewerInvitations.title')}
        </Typography>
      </AccordionSummary>
      <Divider />
      {renderBody()}
    </Accordion>
  )
}

export default ViewerInvitations
